import * as utils from '@owl-nest/utils'
import * as cold from '@owl-nest/cold-data/client'

// Return an object of 2 strings :
// - h1: a single "french human redable" tag (selection based on priority)
// - h2: a "french human readable" string including all tags present in the URL (except categories, sizes and colors)
export function getSEOTexts(
  gendersArray: string[] | undefined,
  typesArray: string[] | undefined,
  brandsArray: string[] | undefined,
  environmentalPillarsArray: string[] | undefined,
  socialPillarsArray: string[] | undefined,
  hierarchy: string | undefined,
  category:
    | {
        name: string
        slug: string
        descriptionShort: string
      }
    | undefined,
): { h1: string; h2: string; htmlTitle: string } {
  const gendersList = cold.taxonomies[cold.types['gender']].children.tags.map((slug) => ({
    value: slug,
    name: cold.tags[slug].name,
  }))
  const typesList = cold.types['product-type']
    .flatMap((slug) => cold.taxonomies[slug].children.tags)
    .map((slug) => ({
      value: slug,
      name: cold.tags[slug].name,
    }))
  const brandsList = Object.values(cold.brands).map((brand) => {
    return {
      value: brand.slug,
      name: brand.name,
    }
  })
  const environmentalPillarsList = cold.taxonomies[cold.types['environmental-pillar']].children.tags.map((item) => ({
    value: item,
    name: cold.tags[item].name,
  }))
  const socialPillarsList = cold.taxonomies[cold.types['social-pillar']].children.tags.map((item) => ({
    value: item,
    name: cold.tags[item].name,
  }))

  // h1SeoText is a one tag string. We use the same priority than the one used for canonical URLs : category > brands > moments > ppillars.
  let h1SeoText = ''
  if (hierarchy && cold.taxonomies[hierarchy]) {
    h1SeoText = cold.taxonomies[hierarchy].name
    if (['femme', 'homme', 'unisexe', 'enfants', 'bebe'].includes(hierarchy)) {
      h1SeoText = `Mode ${h1SeoText}`
    }
  } else if (category) {
    h1SeoText = category.name
  } else if (brandsArray) {
    h1SeoText = `Nos produits ${getQueryParamsToString([brandsArray[0]], brandsList, false, false)}`
  } else if (environmentalPillarsArray) {
    h1SeoText = `Nos produits ${getQueryParamsToString(
      [environmentalPillarsArray[0]],
      environmentalPillarsList,
      true,
      false,
    )}`
  } else if (socialPillarsArray) {
    h1SeoText = `Nos produits ${getQueryParamsToString([socialPillarsArray[0]], socialPillarsList, true, false)}`
  }

  // title is the htmlTitle that will be set on the result page
  const title = category ? `Nos produits ${category.name}` : h1SeoText

  // h2SeoText is a string containing all tags present in the URL if no category or category only in the other case.
  let h2SeoText = ''
  if (category && !hierarchy) {
    h2SeoText = category.descriptionShort
  } else {
    // On ne souhaite afficher que 15 tags max d'où l'algo suivant
    const LIMIT = 15
    const tagsAndFilters = [
      { tags: gendersArray, filters: gendersList },
      { tags: typesArray, filters: typesList },
      { tags: brandsArray, filters: brandsList },
      { tags: environmentalPillarsArray, filters: environmentalPillarsList },
      { tags: socialPillarsArray, filters: socialPillarsList },
    ]

    let resultLength = 0
    let tagsToStringsArray = []

    for (const tagAndFilter of tagsAndFilters) {
      tagsToStringsArray.push(
        getQueryParamsToString(tagAndFilter.tags?.slice(0, LIMIT - resultLength), tagAndFilter.filters),
      )
      resultLength = tagAndFilter.tags ? tagAndFilter.tags.length + resultLength : resultLength

      if (resultLength === LIMIT) break
    }

    const tags = tagsToStringsArray.reduce((result, currentValue) => {
      return result ? (currentValue ? `${result}, ${currentValue}` : result) : currentValue
    })
    if (tags) {
      h2SeoText = `Découvrez tous les produits ${tags}${
        resultLength >= 15 ? ', ...' : ''
      } bien pour la planète et les humains`
    }
  }

  return { h1: h1SeoText, h2: h2SeoText, htmlTitle: title }
}

// Returns a "french human readable" string of tags separated by coma from a list of tags slugs
function getQueryParamsToString(
  tagsArray: string[] | undefined,
  filters:
    | {
        value: string
        name: string
      }[]
    | undefined,
  toLowerCase = true,
  withQuote = true,
): string {
  const result =
    tagsArray && filters
      ? tagsArray?.reduce(
          (result, tag) =>
            `${result ? `${result}, ` : ''}${withQuote ? '"' : ''}${filters?.find((filter) => filter.value === tag)
              ?.name}${withQuote ? '"' : ''}`,
          '',
        )
      : ''
  return toLowerCase ? result.toLowerCase() : result
}
